<template>
  <div class="omnisearch" :class="rootModifierClass">
    <sp-quantum-select
      ref="select"
      :aria-label="ariaLabel"
      class="omnisearch__select"
      navigation="accordion"
      selectable-branches
      fuzzy-search
      :branch-toggle-label="branchToggleLabel"
      location="bottom-left"
      start-on-root
      close-on-select
      hide-search-bar
      show-search-bar-in-sheet
      :max-results="MAX_RESULTS"
      :item-test-id="TestId.finder.suggestion"
      :input-test-id="TestId.finder.sheetSearchInput"
      :items.prop="models"
      :selected="selectedValues"
      :search="searchQuery"
      :open="open"
      :loading="loading"
      :highlight-first-result="showHint"
      @update-selected-item="handleUpdateSelectedItem"
      @update-search="handleUpdateSearch"
      @update-open="handleUpdateOpen"
      @focusin="handleSelectFocusIn"
      @focusout="handleSelectFocusOut"
    >
      <sp-text-field
        ref="input"
        slot="activator"
        v-model="inputText"
        class="omnisearch__input"
        :input-test-id="TestId.finder.searchInput"
        variant="plain"
        prepend-icon="search-bold-filled"
        autocomplete="off"
        maxlength="50"
        clearable
        persistent-clear
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        @input="handleInput"
        @click-clear="handleClear"
        @keydown="handleKeyDown"
        @keydown.enter.stop="handleKeyDownEnter"
        @keydown.space.stop
        @focusin.self="handleFocusIn"
      >
        <!--
          Custom clear affordance: the default SpTextField clear renders a small,
          near-black close glyph that only appears on hover. The landing search
          needs it always visible (mobile has no hover) at a larger size and the
          muted grey of the legacy hero input, so we slot our own icon and size /
          tint it here. The wrapping `.sp-text-field-clear` still drives the clear.
        -->
        <sp-icon
          slot="clear"
          class="omnisearch__clear-icon"
          name="close"
          role="button"
          tabindex="0"
          :data-testid="TestId.finder.clear"
          :aria-label="clearLabel"
          @mousedown.stop.prevent
          @click.stop="handleClear"
          @keydown.enter.stop.prevent="handleClear"
          @keydown.space.stop.prevent="handleClear"
        ></sp-icon>
      </sp-text-field>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { booleanAttribute, toBoolean, trimToUndefined } from "#ce/utils/props";
import { firstDetail, isElementReport } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { useQuantumSearchField } from "#ce/shared/filters/composables/useQuantumSearchField";
import { useOmnisearchSuggestions } from "#ce/apps/SpFinderOmnisearch/composables/useOmnisearchSuggestions";
import { useOmnisearchModels } from "#ce/apps/SpFinderOmnisearch/composables/useOmnisearchModels";
import { useOmnisearchSelection } from "#ce/apps/SpFinderOmnisearch/composables/useOmnisearchSelection";
import { useOmnisearchStore } from "#ce/apps/shared/store/omnisearch-store";

// The omnisearch dropdown caps how many search results it renders. The picker's
// `max-results` is the cap; a pinned chatterbox suggestion counts toward it.
const MAX_RESULTS = 50;

// The landing omnisearch launcher: a single search box over the full omnisearch
// item set (skills + providers + locations + visit/provider types + recent +
// saved + a chatterbox NLP fallback). It owns no routing — the wizard consumes
// the events and decides what to do.
//
//   - `select`      — a result was chosen; detail is its routing payload + title.
//   - `clear`       — the field was emptied.
//   - `text-change` — the typed free-text changed; detail is the raw text. Lets
//                     the wizard keep its `query` filter in sync so submitting
//                     without picking a suggestion still runs that search.
//   - `submit`      — Enter pressed with no item to pick (free-text submit).
const emit = defineEmits(["select", "clear", "text-change", "submit"]);

const props = defineProps({
  /**
   * The doctor-finder embed uuid → selects the shared omnisearch store instance
   * (one fetch per embed). Placeholder / aria / required-skill / saved searches
   * all arrive through the store's `configure` (called by the wizard), not props.
   */
  uuid: {
    type: String,
    default: undefined,
  },
  /**
   * Accessible label for the accordion branch disclosure toggle (the +/− control
   * that expands a parent specialty's subspecialties). Pass a translated string;
   * `aria-expanded` conveys the open/closed state.
   */
  branchToggleLabel: {
    type: String,
    default: undefined,
  },
  /**
   * Accessible label for the clear (X) button that empties the search field.
   * Pass a translated string.
   */
  clearLabel: {
    type: String,
    default: undefined,
  },
  /**
   * When set, focusing the search input (e.g. tabbing into it) opens the
   * suggestion dropdown immediately, without moving focus into the list — the
   * optional open-on-focus combobox behaviour. Off by default; the arrow keys
   * always open the dropdown regardless.
   *
   * @type {Boolean|String}
   */
  openOnFocus: booleanAttribute(),
});

const store = useOmnisearchStore(trimToUndefined(props.uuid));

const placeholder = computed(() => store.config.value?.placeholder ?? "");
const ariaLabel = computed(() => store.config.value?.ariaLabel ?? "");

// While the suggestion fetch is in flight the item set is empty; the picker's
// loading state keeps an early open (click/type during first load) from
// reading as "No data".
const loading = store.loading;

const rootModifierClass = computed(() => ({ "--is-loading": loading.value }));

const inputEl = useTemplateRef("input");
const selectEl = useTemplateRef("select");

const { inputText, searchQuery, open, applyInput, applyClear, applyOpen, applyActivatorKeydown, flushSearch, setText } =
  useQuantumSearchField();

// Move roving focus into the just-opened list by calling the picker directly.
function focusList(location) {
  selectEl.value?.focusListItem?.(location);
}

function handleKeyDown(event) {
  applyActivatorKeydown(event, focusList);
}

// Open-on-focus (opt-in): `.self` limits this to the inner input (not the slotted
// clear icon); a programmatic refocus via `focusInput` is skipped so a just-closed
// menu is not reopened.
function handleFocusIn() {
  if (consumeSuppressedFocusOpen()) {
    return;
  }
  if (toBoolean(props.openOnFocus)) {
    open.value = true;
  }
}

const {
  selected,
  selectedValues,
  customValidity,
  consumeSuppressedFocusOpen,
  hasSelection,
  setCustomValidity,
  reportValidity,
  focusInput,
  openSearch,
} = useOmnisearchSelection({ inputEl, open });

// The chatterbox suggestion is paired with the query it was fetched for, so
// `models` appends it only under its originating query.
const {
  suggestion,
  request: requestSuggestion,
  clear: clearSuggestion,
} = useOmnisearchSuggestions({
  store,
  searchQuery,
});

const { models } = useOmnisearchModels({ store, searchQuery, suggestion });

// Whether roving focus is inside the picker's dropdown. focus events are
// composed, so they cross the shadow boundary: an event from the picker's own
// shadow DOM (list rows, the in-sheet search bar) is retargeted to the picker
// host itself, while one from the slotted activator keeps the activator as its
// target. Used to drop the typing hint once a row has real focus styling.
const listFocused = ref(false);

function handleSelectFocusIn(event) {
  listFocused.value = event.target === event.currentTarget;
}

function handleSelectFocusOut() {
  listFocused.value = false;
}

// A closed dropdown has no focusable rows, but the closing may eat the focusout.
watch(open, (isOpen) => {
  if (!isOpen) {
    listFocused.value = false;
  }
});

// Mark the picker's first result as the combobox's pending choice — the row
// Enter will pick — while the user is typing in the field. Once roving focus
// moves into the list the row's real focus styling takes over, so the hint is
// dropped and two rows are never highlighted at once. The picker itself only
// marks while a query is active, so idle browsing never shows a hint.
const showHint = computed(() => open.value && !listFocused.value);

function handleInput(event) {
  // The directive re-dispatches a plain `input` when a composition ends; the field has already
  // reported that edit, so acting on it runs the whole typing pipeline a second time.
  if (!isElementReport(event)) {
    return;
  }

  const text = applyInput(event);

  selected.value = null;
  customValidity.value = "";
  requestSuggestion(text.trim());
  // Surface the typed free-text so the wizard can keep its `query` filter in
  // sync — a search submitted without picking a suggestion still runs it.
  emit("text-change", text);
}

// The picker's in-sheet search bar — which replaces the sheet-covered activator
// on small screens — shares the activator's typing pipeline, so a sheet search
// runs over the full omnisearch item set and stays in the field after the sheet
// closes for a free-text submit.
function handleUpdateSearch(event) {
  handleInput(event);
}

// Enter in the activator commits the picker's first result (the row the typing
// hint marks) when the dropdown is showing one; the free-text `submit` is the
// fallback when there is nothing to pick. The pending search debounce is
// flushed first (and one render awaited) so the committed row always belongs
// to the text as typed, never to a query the debounce hadn't caught up with.
async function handleKeyDownEnter() {
  if (!inputText.value.trim()) {
    return;
  }

  if (open.value) {
    flushSearch();
    await nextTick();
    const candidate = selectEl.value?.firstResult?.();
    if (candidate) {
      commitSelection(candidate);
      open.value = false;
      return;
    }
  }

  emit("submit");
}

function handleUpdateOpen(event) {
  // An opening sheet seeds its search bar from `searchQuery`, so the text typed
  // before it opened must already be there.
  if (applyOpen(event)) {
    flushSearch();
  }
}

function handleClear() {
  applyClear();
  clearSuggestion();
  selected.value = null;
  emit("clear");
}

function handleUpdateSelectedItem(event) {
  const item = firstDetail(event);
  if (!item) {
    return;
  }

  commitSelection(item);
}

// Commit a chosen row — via click/keyboard in the list or Enter on the first
// result: remember it (required-skill validation reads `hasSelection`), echo its
// text into the field, and surface the routing payload. `selectionText`
// overrides the display title for rows whose label decorates the committed text
// (the quoted phrase-search row).
function commitSelection(item) {
  selected.value = item;
  setText(item.selectionText ?? item.title ?? "");
  customValidity.value = "";

  emit("select", { title: item.title, ...(item.payload ?? {}) });
}

const { exposeProperties, exposeMethods } = useExpose();

// The required-skill gate stays in the wizard's submit handler; it reads
// `hasSelection` and drives validity through these methods so it never has to
// reach into this element's shadow DOM.
exposeProperties({
  hasSelection: {
    get: hasSelection,
  },
});

exposeMethods({ setCustomValidity, reportValidity, focusInput, openSearch });
</script>

<style>
:host {
  display: block;
  width: 100%;

  /* Saved-search rows render `showFilters` labels as sp-list-item tag chips. Map
     the list-item tag tokens to themed system tokens (a faint primary-container
     fill) so the chips theme per-client. Custom properties inherit through the
     nested shadow boundaries down to each sp-list-item. */
  --sp-comp-list-item-tag-background-color: var(--sp-sys-color-primary-container);
  --sp-comp-list-item-tag-color: var(--sp-sys-color-on-primary-container);
  --sp-comp-list-item-tag-radius: var(--sp-ref-border-radius-xs);
  --sp-comp-list-item-tag-font-size: var(--sp-sys-typescale-small-font-size);
  --sp-comp-list-item-tags-gap: var(--sp-ref-scale-4);

  /* The specialty glyph fills ~80% of its coloured circle (filled icon variant).
     Affects only glyph-on-circle rows; colour-variant clients (no circle) are
     unaffected. */
  --sp-comp-list-item-prepend-bg-icon-ratio: 0.8;

  /* The dropdown hugs its content (legacy-spotlight parity): without this the
     shell's 25rem popup min-height leaves a short result list — or a lone
     phrase-search row — floating at the top of a mostly empty panel. The
     picker's default ceiling reads the same token, so zeroing the floor alone
     would collapse the dropdown to 0px — the adapter below restores it. */
  --sp-comp-quantum-select-popup-min-height: 0;
  --sp-comp-quantum-select-popup-max-height: var(--sp-comp-omni-search-popup-max-height, 25rem);

  /* Dropdown surface radius, adapted to an omnisearch-scoped token so a client
     can round just this dropdown (e.g. Scripps' pill-shaped fields) without
     retheming every sp-menu popup. Defaults to the popup's own 0.5rem. */
  --sp-comp-menu-border-radius: var(--sp-comp-omni-search-menu-border-radius, var(--sp-ref-border-radius-sm));
}

/* While the store's first fetch is in flight the panel holds only the centered
   spinner, and the hug would shrink-wrap it. Half the ceiling ≈ a short result
   list, so the resolve resizes least in either direction. */
.omnisearch.--is-loading {
  --sp-comp-quantum-select-popup-min-height: 12.5rem;
}
</style>

<style lang="scss" scoped>
.omnisearch,
.omnisearch__select,
.omnisearch__input {
  width: 100%;
}

// Tint the leading magnifier the same neutral colour as the legacy search-hero
// input. The external `::part(prepend-icon)` rule in _search_input.scss can't
// pierce this component's nested shadow, so without this the icon falls through
// to the sp-icon default (brand primary). Same token chain as that rule.
.omnisearch__input::part(prepend-icon) {
  --fill: var(--sp-comp-omni-search-icon-fill, var(--sp-comp-filter-default-activator-icon-fill-color));
}

// Slotted clear (X) icon — larger than the default small glyph and in a muted
// grey rather than near-black. The muted `on-surface-variant` token is used (not
// `on-surface`, which several client themes set to near-black — the very colour
// we're moving away from). `--sp-comp-icon-size` drives the size because no
// `size` attribute is set on the icon (a size class would otherwise win over the
// custom property).
.omnisearch__clear-icon {
  --sp-comp-icon-size: 1.75rem;
  --sp-comp-icon-fill: var(--sp-comp-omni-search-clear-icon-fill, var(--sp-sys-color-on-surface-variant, #666));
  cursor: pointer;
}
</style>
